<template>
  <div
    class="picker-backdrop"
    v-if="show"
    v-on:click.self="$emit('update:show', false)"
    v-on:dragover.prevent
    v-on:drop.prevent="dragging = false"
  >
    <div class="picker-panel" v-on:dragover.prevent="dragging = true">
      <div class="picker-head">
        <h5>Select video</h5>
        <span class="picker-count" v-if="videos.length">
          {{ videos.length }} {{ videos.length === 1 ? 'video' : 'videos' }}
        </span>
        <button
          type="button"
          class="picker-close"
          v-on:click="$emit('update:show', false)"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path
              d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
            />
          </svg>
        </button>
      </div>

      <div class="picker-tools">
        <div class="picker-searchwrap">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path
              d="M18.031 16.617l4.283 4.282-1.415 1.415-4.282-4.283A8.96 8.96 0 0 1 11 20c-4.968 0-9-4.032-9-9s4.032-9 9-9 9 4.032 9 9a8.96 8.96 0 0 1-1.969 5.617zm-2.006-.742A6.977 6.977 0 0 0 18 11c0-3.868-3.133-7-7-7-3.868 0-7 3.132-7 7 0 3.867 3.132 7 7 7a6.977 6.977 0 0 0 4.875-1.975l.15-.15z"
            />
          </svg>
          <input
            ref="pickerSearch"
            class="picker-search"
            type="text"
            v-model="q"
            placeholder="Search videos…"
            v-on:input="debouncedLoadVideos"
            @mousedown.stop
            @click.stop
          />
        </div>

        <button
          type="button"
          class="picker-upload"
          v-on:click="uploadMenu = !uploadMenu"
          :class="{ 'is-active': uploadMenu }"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path
              d="M12 12.586l4.243 4.242-1.415 1.415L13 16.415V22h-2v-5.587l-1.828 1.83-1.415-1.415L12 12.586zM12 2a7.001 7.001 0 0 1 6.954 6.194A5.5 5.5 0 0 1 18 18.978v-2.014a3.5 3.5 0 1 0-1.5-6.664A5 5 0 1 0 7 9c0 .34.034.672.1.994A3.5 3.5 0 0 0 6 16.964v2.014a5.5 5.5 0 0 1-.954-10.784A7.001 7.001 0 0 1 12 2z"
            />
          </svg>
          Upload
        </button>
      </div>

      <div class="picker-error" v-if="uploadError">{{ uploadError }}</div>

      <div class="picker-body">
        <div class="picker-upload-area" v-if="uploadMenu">
          <div class="picker-converting" v-if="isConverting">
            <div class="picker-converting-icon">
              <svg
                viewBox="0 0 24 24"
                width="32"
                height="32"
                fill="currentColor"
              >
                <path
                  d="M12 4V1L8 5l4 4V6a6 6 0 0 1 6 6c0 1.1-.3 2.1-.8 3l1.5 1.5A8 8 0 0 0 12 4zm0 14a6 6 0 0 1-6-6c0-1.1.3-2.1.8-3L5.3 7.5A8 8 0 0 0 12 20v3l4-4-4-4v3z"
                />
              </svg>
            </div>
            <div class="picker-converting-text">
              <h6>Upload complete!</h6>
              <p>
                The video is now being converted. You can check the progress at
                any time in the <strong>header status menu</strong>.
              </p>
              <button
                type="button"
                class="btn-finish"
                v-on:click="isConverting = false"
              >
                Back to library
              </button>
            </div>
          </div>
          <file-pond
            v-else
            name="video"
            ref="pond"
            label-idle="Drop files here or <span class='filepond--label-action'>Browse</span>"
            v-bind:allow-multiple="false"
            accepted-file-types="video/mp4, video/avi, video/ogg, video/webm, video/quicktime, video/x-quicktime, video/x-msvideo"
            v-bind:files="myFiles"
            :server="server"
            :chunkUploads="true"
            :chunkForce="true"
            :chunkSize="1000000"
            :credits="false"
          />
        </div>

        <div class="picker-loading" v-if="loading">Loading videos…</div>
        <div class="picker-none" v-else-if="videos.length === 0">
          No videos found.
        </div>
        <div class="picker-grid" v-else>
          <div
            class="picker-card"
            v-for="video in videos"
            :key="video.id"
            :class="{ 'is-current': isCurrentVideo(video) }"
          >
            <button
              type="button"
              class="picker-pick"
              title="Use this video"
              v-on:click="selectVideo(video)"
            >
              <img
                :src="mediaUrl + '/' + video.path + '/frame1.jpg'"
                :alt="video.name"
                v-on:error="onPosterError($event)"
              />
              <div class="picker-name">{{ video.name }}</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vueFilePond from "vue-filepond";
import "filepond/dist/filepond.min.css";

import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import http from "../../modules/http";
import { recordProblem } from "../../modules/problem-log";
import tenant from "../../modules/tenant";

// Create component
const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginImagePreview
);

export default {
  name: "VideoUpload",
  props: {
    show: Boolean,
    value: null,
  },
  computed: {
    mediaUrl() {
      return tenant.getAppMediaUrl().replace("/images", "/videos");
    },
  },
  data: function () {
    return {
      uploadMenu: false,
      uploadError: "",
      myFiles: [],
      videos: [],
      uploading: false,
      isConverting: false,
      loading: false,
      q: "",
      dragging: false,
      server: {
        process: this.upload,
        revert: process.env.VUE_APP_MEDIA_API + "/videos/revert",
        restore: process.env.VUE_APP_MEDIA_API + "/videos/restore",
        load: process.env.VUE_APP_MEDIA_API + "/videos/load",
        fetch: process.env.VUE_APP_MEDIA_API + "/videos/upload/a",
      },
    };
  },
  created() {
    this.debouncedLoadVideos = this.debounce(this.loadVideos, 500);
  },
  watch: {
    show(val) {
      if (val) {
        this.loadVideos();
        this.$nextTick(() => {
          this.$refs.pickerSearch?.focus();
        });
      }
    },
  },
  methods: {
    debounce(fn, delay) {
      let timeout = null;
      return function (...args) {
        if (timeout) {
          clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
          fn.apply(this, args);
        }, delay);
      };
    },
    loadVideos() {
      this.loading = true;
      const q = this.q.trim();
      const request =
        q.length > 0
          ? http.fetch("/videos/search", { q: q })
          : http.fetch("/videos");

      request
        .then((data) => {
          this.videos = Array.isArray(data) ? data : [];
          this.loading = false;
        })
        .catch((err) => {
          this.uploadError = "The video library could not be loaded.";
          recordProblem(
            "editor",
            "Video library could not be loaded",
            err?.message ?? String(err)
          );
          this.loading = false;
        });
    },
    isCurrentVideo(video) {
      if (!this.value || !video) return false;
      return this.value.path && this.value.path.includes(video.path);
    },
    selectVideo(video) {
      let obj = Object.assign({}, video);
      obj.path = this.mediaUrl + "/" + obj.path;
      this.$emit("select", obj);
      this.$emit("update:show", false);
    },
    upload(
      fieldName,
      file,
      metadata,
      load,
      error,
      progress,
      abort,
      transfer,
      options
    ) {
      const _this = this;
      _this.uploading = true;
      const request = new XMLHttpRequest();

      const id = Math.round(new Date() / 1000);
      let send = { type: "cover", id: id, videos: [file.name] };

      http
        .fetch("/files/upload", send)
        .then((data) => {
          const formData = new FormData();
          formData.append("token", data.token);
          formData.append("album", id);
          formData.append("category", "cover");

          request.open(
            "POST",
            process.env.VUE_APP_MEDIA_API + "/videos/upload"
          );

          request.setRequestHeader("Upload-Length", file.size);
          request.setRequestHeader(
            "Upload-Name",
            encodeURIComponent(file.name)
          );

          request.upload.onprogress = (e) => {
            progress(e.lengthComputable, e.loaded, file.size);
          };

          request.onload = function () {
            if (request.status >= 200 && request.status < 300) {
              transfer(request.responseText);

              _this.chunkUpload(
                0,
                file,
                options,
                fieldName,
                data,
                progress,
                error,
                load,
                request.responseText
              );
            } else {
              _this.uploading = false;
              error("oh no");
            }
          };

          request.onerror = function () {
            _this.uploadError = "The upload failed. Check your connection.";
            recordProblem("editor", "Video upload failed (network)", file.name);
            error("network");
          };

          request.send(formData);
        })
        .catch((err) => {
          const detail = err?.message ?? String(err);
          _this.uploadError = `The server refused the video (${detail}).`;
          recordProblem(
            "editor",
            "Video upload refused",
            `${file.name}: ${detail}`
          );
          error(detail);
        });

      return {
        abort: () => {
          _this.uploading = false;
          request.abort();
          abort();
        },
      };
    },
    chunkUpload(
      start,
      file,
      options,
      fieldName,
      data,
      progress,
      error,
      load,
      fileId,
      i = 0
    ) {
      let _this = this;
      if (start < file.size) {
        let chunkEnd = Math.min(start + options.chunkSize, file.size);
        let chunkSize = chunkEnd - start;
        const chunk = file.slice(start, chunkEnd);

        const chunkForm = new FormData();
        chunkForm.append(fieldName, chunk, file.name);
        chunkForm.append("token", data.token);
        chunkForm.append("album", data.album);
        chunkForm.append("category", "cover");
        chunkForm.append("_method", "PATCH");

        const request = new XMLHttpRequest();

        request.open(
          "POST",
          process.env.VUE_APP_MEDIA_API + "/videos/upload/" + fileId
        );

        request.setRequestHeader("Upload-Offset", start);
        request.setRequestHeader("Upload-Length", chunkSize);
        request.setRequestHeader("Upload-Name", encodeURIComponent(file.name));

        request.upload.onprogress = (e) => {
          progress(e.lengthComputable, start + e.loaded, file.size);
        };

        request.onload = function () {
          if (request.status >= 200 && request.status < 300) {
            _this.chunkUpload(
              chunkEnd,
              file,
              options,
              fieldName,
              data,
              progress,
              error,
              load,
              fileId
            );
          } else {
            if (i >= options.chunkRetryDelays.length) {
              _this.uploading = false;
              error("oh no");
            } else {
              setTimeout(() => {
                _this.chunkUpload(
                  start,
                  file,
                  options,
                  fieldName,
                  data,
                  progress,
                  error,
                  load,
                  fileId,
                  i + 1
                );
              }, options.chunkRetryDelays[i]);
            }
          }
        };

        request.onerror = function () {
          recordProblem(
            "editor",
            "Video upload broke off",
            `${file.name} at ${start} bytes`
          );
          error("network");
        };

        request.send(chunkForm);
      } else {
        load();
        _this.uploading = false;
        _this.uploaded(file.name);
      }
    },
    uploaded() {
      this.isConverting = true;
      this.loadVideos();
    },
    onPosterError(e) {
      e.target.src = "/loading.gif";
    },
  },
  emits: ["update:show", "select"],
  components: { FilePond },
};
</script>

<style scoped>
.picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  pointer-events: auto;
}
.picker-panel {
  position: relative;
  width: min(1140px, 100%);
  max-height: min(880px, 92vh);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.3);
  overflow: hidden;
  pointer-events: auto;
}
.picker-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 20px;
  border-bottom: 1px solid #eef2f7;
}
.picker-head h5 {
  margin: 0;
  font-size: 15.5px;
  font-weight: 700;
  color: #1e293b;
}
.picker-count {
  font-size: 12.5px;
  font-weight: 600;
  color: #94a3b8;
}
.picker-close {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  line-height: 0;
  padding: 5px;
  border-radius: 7px;
}
.picker-close:hover {
  background: #f1f5f9;
  color: #475569;
}
.picker-tools {
  display: flex;
  gap: 10px;
  padding: 14px 20px 0;
}
.picker-searchwrap {
  position: relative;
  flex: 1;
}
.picker-searchwrap svg {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  z-index: 3;
  pointer-events: none;
}
.picker-search {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 9px 12px 9px 34px;
  font-size: 14px;
  outline: none;
  position: relative;
  z-index: 2;
}
.picker-search:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}
.picker-upload {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  border-radius: 9px;
  background: #f1f5f9;
  color: #475569;
  font-size: 13.5px;
  font-weight: 600;
  padding: 0 16px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}
.picker-upload:hover {
  background: #e2e8f0;
}
.picker-upload.is-active {
  background: #7c3aed;
  color: #fff;
}
.picker-error {
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12.5px;
  margin: 10px 20px 0;
}
.picker-body {
  flex: 1;
  overflow-y: auto;
  padding: 15px 20px 20px;
}
.picker-upload-area {
  margin-bottom: 20px;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  padding: 10px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.picker-converting {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
}
.picker-converting-icon {
  color: #7c3aed;
  animation: spin 3s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.picker-converting-text h6 {
  margin: 0 0 4px 0;
  font-weight: 700;
  color: #1e293b;
}
.picker-converting-text p {
  margin: 0 0 12px 0;
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.5;
}
.btn-finish {
  border: 0;
  background: #7c3aed;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 7px;
  cursor: pointer;
}
.btn-finish:hover {
  background: #6d28d9;
}
.picker-loading,
.picker-none {
  padding: 40px;
  text-align: center;
  color: #64748b;
  font-size: 14.5px;
}
.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 15px;
}
.picker-card {
  position: relative;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
}
.picker-card:hover {
  border-color: #7c3aed;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.1);
}
.picker-card.is-current {
  border-color: #7c3aed;
  border-width: 2px;
}
.picker-pick {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
}
.picker-pick img {
  width: 100%;
  aspect-ratio: 16/9;
  object-fit: cover;
  display: block;
}
.picker-name {
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
